import React, { useState, useTransition, Suspense } from 'react'
import { LayoutList, Activity, Sigma, Target, BarChart2 } from 'lucide-react'
import { TableSkeleton } from './ui/TableSkeleton'
import { ErrorBoundary } from './ui/ErrorBoundary'

// ═══════════════════════════════════════════════════════════════════
// PERMANENT FIX: LAZY-LOAD ALL TAB CONTENT
//
// Previously, DashboardHistoryTable was imported eagerly. This meant
// its heavy `useAnalysisHistory` hook (which subscribes to DataBus
// and runs `calculateAnalysis()` for every snapshot in a loop)
// mounted DURING the click navigation — causing a 921ms freeze.
//
// Now: Every tab panel is lazy-loaded. The module + hook only mount
// AFTER the browser has finished painting the navigation skeleton.
// ═══════════════════════════════════════════════════════════════════
const DashboardHistoryTable = React.lazy(() =>
    import('./tables/DashboardHistoryTable').then(m => ({ default: m.DashboardHistoryTable }))
)
const DebugVarianceWidget = React.lazy(() =>
    import('./DebugVarianceWidget').then(m => ({ default: m.DebugVarianceWidget }))
)
const DebugCountWidget = React.lazy(() =>
    import('./DebugCountWidget').then(m => ({ default: m.DebugCountWidget }))
)
// CoG tab content — lazy + self-contained (fetches its own data)
const CogTabContent = React.lazy(() => import('./CogTabContent'))

interface DashboardTableSwitcherProps {
    settings: {
        symbol: string
        expiry: string
        mode: string
        range: number
    }
    marketData: any
    isMarketLoading: boolean
    analysis: any
}

// Fallback skeleton shown while lazy modules load
const TabSkeleton = () => (
    <div className="mx-4 animate-pulse">
        <div className="h-[400px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 border border-slate-200/50 dark:border-slate-700/50" />
    </div>
)

export const DashboardTableSwitcher: React.FC<DashboardTableSwitcherProps> = ({
    settings,
    marketData,
    isMarketLoading,
    analysis
}) => {
    const [isPending, startTransition] = useTransition()
    // Determine active tab via persistent state to keep it through re-renders
    const [activeTab, setActiveTab] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('dashboard_active_tab') || 'history'
        }
        return 'history'
    })

    const handleTabChange = (tabId: string) => {
        startTransition(() => {
            setActiveTab(tabId)
        })
        if (typeof window !== 'undefined') localStorage.setItem('dashboard_active_tab', tabId)
    }

    // PERMANENT FIX: useCogHistoryBus REMOVED from here.
    // Previously it ran on EVERY mount regardless of active tab, triggering
    // DataBus subscription + heavy useMemo on marketData.options.
    // Now the CoG data fetching lives INSIDE CogTabContent (lazy-loaded).

    const tabs = [
        { id: 'history', label: 'Intraday History', icon: LayoutList },
        { id: 'variance', label: 'OI Variance', icon: Activity },
        { id: 'counts', label: 'Rank Counts', icon: Sigma },
        { id: 'cog', label: 'CoG History', icon: Target },
    ]

    return (
        <div className="flex flex-col gap-4 animate-in fade-in duration-500">
            {/* Header / Toolbar Card */}
            <div className="mx-4 p-3 bg-white dark:bg-slate-900/60 backdrop-blur-xl border border-slate-200/60 dark:border-slate-800/60 rounded-2xl shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-all overflow-hidden relative">
                {/* Subtle Background Gradient for Card */}
                <div className="absolute inset-0 bg-linear-to-r from-indigo-50/30 to-purple-50/20 dark:from-indigo-900/10 dark:to-purple-900/5 pointer-events-none" />

                {/* Left Side: Icon & Titles */}
                <div className="flex items-center gap-3.5 relative z-10 pl-1">
                     <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-800/50">
                         <BarChart2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                     </div>
                     <div>
                         <h2 className="text-[17px] font-bold text-slate-900 dark:text-white leading-tight">Dashboard Analysis</h2>
                         <p className="text-[12px] font-medium text-slate-500 dark:text-slate-400 mt-0.5">Deep dive into strikes and flow</p>
                     </div>
                </div>

                {/* Right Side: Segmented Control Tabs */}
                <div className="p-1 bg-slate-50 dark:bg-slate-800/60 rounded-xl flex items-center gap-1 border border-slate-200/50 dark:border-slate-700/50 relative z-10 overflow-x-auto max-w-full hide-scrollbar">
                    {tabs.map((tab) => {
                        const Icon = tab.icon
                        const isActive = activeTab === tab.id
                        
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`
                                    flex items-center gap-2 px-4 py-2 text-[13px] font-bold rounded-lg whitespace-nowrap transition-all duration-300
                                    ${isActive 
                                        ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm border border-slate-200/50 dark:border-slate-700/50' 
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}
                                `}
                            >
                                <Icon className={`h-4 w-4 ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'opacity-60'}`} />
                                {tab.label}
                            </button>
                        )
                    })}
                </div>
            </div>

            <div className="transition-all duration-300 relative min-h-[400px]">
                <ErrorBoundary name="DashboardWidgets">
                    <Suspense fallback={<TabSkeleton />}>
                        {activeTab === 'history' && (
                            <DashboardHistoryTable
                                settings={settings}
                                marketData={marketData}
                                isHistoryLoading={isMarketLoading}
                            />
                        )}

                        {activeTab === 'variance' && (
                            <div className="animate-in slide-in-from-bottom-2 fade-in duration-300">
                                <DebugVarianceWidget />
                            </div>
                        )}

                        {activeTab === 'counts' && (
                            <div className="animate-in slide-in-from-bottom-2 fade-in duration-300">
                                <DebugCountWidget />
                            </div>
                        )}

                        {activeTab === 'cog' && (
                            <div className="mx-4 mb-4 animate-in slide-in-from-bottom-2 fade-in duration-300">
                                <CogTabContent
                                    settings={settings}
                                    analysis={analysis}
                                    marketData={marketData}
                                />
                            </div>
                        )}
                    </Suspense>
                </ErrorBoundary>
            </div>
        </div>
    )
}
